import { GetServerSidePropsContext } from 'next';
import React from 'react';
import ShopSearchHead from 'components/ShopSearchHead/ShopSearchHead';
import { DefaultPage } from 'styles/Page.styles';
import BasicLayout from 'layouts/BasicLayout';
import { CategoryPageInputProps } from 'models/CategoryPageProps';
import { FilterableQueryParams } from 'models/FilterableQueryParams';
import { ShopSearchRawQueryParams } from 'models/ShopSearchRawQueryParams';
import { getModals } from 'helpers/getModals';
import { getResultPageComponents } from 'helpers/getResultPageComponents';
import { getMainPageComponents } from 'helpers/getMainPageComponents';
import { getSearchResults } from 'helpers/getSearchResults';
import { getMainPageCategories } from 'helpers/getMainPageCategories';
import { GetInitialIndexPageProps } from 'models/GetInitialIndexPageProps';
import { IndexPageInputProps } from 'models/IndexPageProps';
import {
  determineEnvironmentFromHost,
  determineHost,
  determineTargetMarketFromHost
} from 'helpers/url-mapper';
import { useIntl } from 'react-intl';
import { TranslateFunction } from 'models/TranslateFunction';
import { getPageLinks } from 'helpers/getPageLinks';
import { emptyPage } from 'helpers/emptyPage';
import { isEmbedActive } from 'helpers/isEmbedActive';

const getInitialProps: GetInitialIndexPageProps = async (
  context: GetServerSidePropsContext
) => {
  const host = determineHost(context);
  const environment = determineEnvironmentFromHost(host);
  const targetMarket = determineTargetMarketFromHost(host);
  const [pageLinks, props] = await Promise.all([
    getPageLinks(context, environment),
    context.query?.q
      ? getSearchResults(context, environment, targetMarket).catch(
          (errorStatusCode) => {
            /* istanbul ignore if */
            if (typeof window === 'undefined') {
              context.res.statusCode = errorStatusCode;
            }
            return emptyPage;
          }
        )
      : getMainPageCategories(environment, targetMarket)
  ]);

  return {
    ...props,
    account: undefined,
    host,
    pageLinks,
    queryParams: context.query as ShopSearchRawQueryParams
  };
};

MainPage.getInitialProps = getInitialProps;

export function MainPage(props: IndexPageInputProps) {
  const { formatMessage } = useIntl();
  const translate: TranslateFunction = (id: string) => formatMessage({ id });

  let componentsToShow;
  let modals = null;
  let queryParams = {};
  const windowTitleParams = {
    id: 'WINDOW_TITLE',
    count: 0
  };
  let activeFilters: FilterableQueryParams;

  if ((props as CategoryPageInputProps).shops) {
    const categoryProps = props as CategoryPageInputProps;
    queryParams = categoryProps.queryParams;

    activeFilters = {
      q: categoryProps.searchTerm,
      page: categoryProps.activePage || undefined,
      sort: categoryProps.sort,
      hasValidCertificate: categoryProps.hasValidCertificate,
      ...(isEmbedActive(categoryProps.queryParams.embed) ? { embed: true } : {})
    };

    componentsToShow = getResultPageComponents({
      activeFilters,
      onParameterChange: categoryProps.onParameterChange,
      category: null,
      setMobileMenuOpen: categoryProps.setMobileMenuOpen,
      mobileMenuOpen: categoryProps.mobileMenuOpen,
      metadata: categoryProps.metadata,
      setModalVisible: categoryProps.setModalVisible,
      categoryList: categoryProps.categoryList,
      shops: categoryProps.shops
    });

    modals = getModals({
      modalVisible: categoryProps.modalVisible,
      setModalVisible: categoryProps.setModalVisible,
      categoryList: categoryProps.categoryList,
      activeFilters,
      onParameterChange: categoryProps.onParameterChange,
      translate
    });

    windowTitleParams.id = 'WINDOW_TITLE_QUERY';
    windowTitleParams.count = categoryProps.metadata.totalShopCount;
  } else {
    props.setModalVisible(null);

    queryParams = props.queryParams ?? {};

    activeFilters = {
      ...(isEmbedActive(props.queryParams?.embed) ? { embed: true } : {})
    };

    componentsToShow = getMainPageComponents({
      onParameterChange: props.onParameterChange,
      setMobileMenuOpen: props.setMobileMenuOpen,
      mobileMenuOpen: props.mobileMenuOpen,
      categoryList: props.categoryList,
      activeFilters
    });
  }

  return (
    <BasicLayout noScrolling={!!props.modalVisible || props.mobileMenuOpen}>
      <DefaultPage
        $hasAside={!!props.categoryList && props.categoryList.length > 0}
      >
        <ShopSearchHead
          {...{
            totalShopCount: windowTitleParams.count,
            queryParams,
            pageLinks: props.pageLinks,
            host: props.host
          }}
        />
        {componentsToShow}
      </DefaultPage>
      {modals}
    </BasicLayout>
  );
}

export default MainPage;
